import React, { useState, useEffect } from "react";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import PageHeader from "@iso/components/utility/pageHeader";
import Box from "@iso/components/utility/box";
import IntlMessages from "@iso/components/utility/intlMessages";
import { Button, Input, Pagination, Popconfirm, Select, Table, Space } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { educationAssetService } from "@iso/services/educationAsset.service";
import { educationTypes } from "@iso/constants/educationTypes";
import EducationAssetModal from "@iso/containers/Pages/PropertyMap/components/Asset/EducationAssetModal";
import moment from "moment";
import Loader from "@iso/components/utility/loader";

const EducationAsset = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );

  useEffect(() => {
    if (activePropertyId) {
      loadAssets();
    }
  }, [activePropertyId, currentPage, pageSize, searchInput, typeFilter]);

  const loadAssets = () => {
    setLoading(true);
    const params = {
      page: currentPage,
      search: searchInput,
      type: typeFilter,
    };

    educationAssetService.getList(activePropertyId, params).then((res) => {
      if (res && res.code === "0000") {
        setAssets(res.education_assets || []);
        setTotalItems(res.total_item || 0);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  };

  const handleDelete = (id) => {
    educationAssetService.destroy(id).then((res) => {
      if (res && res.code === "0000") {
        loadAssets();
      }
    });
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditingAsset(null);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingAsset(null);
  };

  const handleModalSuccess = () => {
    loadAssets();
    handleModalClose();
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "10%",
      render: (type) => {
        const typeObj = educationTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%",
      ellipsis: true,
    },
    {
      title: "Primary Trainer",
      dataIndex: "primary_trainer_name",
      key: "primary_trainer_name",
      width: "15%",
    },
    {
      title: "Active Date",
      dataIndex: "active_date",
      key: "active_date",
      width: "10%",
      render: (date) => date ? moment(date).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      width: "10%",
      render: (date) => date ? moment(date).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {(activePermission === "owner" || activePermission === "admin") && (
            <Popconfirm
              title="Are you sure you want to delete this education asset?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <LayoutWrapper>
      <PageHeader>Education Assets</PageHeader>
      <Box>
        <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <Input
            placeholder="Search education assets"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="Filter by type"
            allowClear
            value={typeFilter}
            onChange={setTypeFilter}
            options={educationTypes}
            style={{ width: 150 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Education Asset
          </Button>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={assets}
              rowKey="id"
              pagination={false}
            />
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalItems}
                onChange={handlePageChange}
                showSizeChanger
                showTotal={(total) => `Total ${total} items`}
              />
            </div>
          </>
        )}

        {modalVisible && (
          <EducationAssetModal
            visible={modalVisible}
            onClose={handleModalClose}
            onSuccess={handleModalSuccess}
            editingAsset={editingAsset}
            propertyId={activePropertyId}
          />
        )}
      </Box>
    </LayoutWrapper>
  );
};

export default EducationAsset;
